/**
 * Navigation Manager — navigation experience orchestrator.
 *
 * TTA v5: NavGo is the routing engine.
 * NavigationManager orchestrates application-level behavior.
 *
 * NavGo owns:
 *   URL parsing, route matching, params, history, popstate
 *
 * NavigationManager owns:
 *   View transitions, motion, scroll, focus, gestures, prefetch, analytics
 *
 * Pages must never call:
 *   - window.history.pushState directly
 *   - NavGo internals directly
 *
 * Everything goes through NavigationManager.
 */

import Navgo from 'navgo'
import { routeConfig } from './routes.js'
import { runTransition } from './transition-engine.js'
import { getTransitionSemantic, getEdgesForPath } from './navigation-graph.js'
import { saveScroll, restoreScroll, resetScroll } from './scroll-manager.js'
import { clearStaleChunkMarker, recoverFromStaleChunk } from './stale-chunk.js'
import { createQuery } from '../data/query-client.svelte.js'
import { loadingManager } from '../loading/loading-manager.js'
import { LoadingType } from '../loading/types.js'
import { RouteSources } from '../loading/sources.js'
import { session, sessionReady } from '../../store.svelte.js'
import { has_app_history } from './history-state.js'

export class NavigationManager {
  /** @type {import('navgo').default} */
  #navgo

  /** @type {import('./types.js').RouteConfig[]} */
  #configs

  /** @type {Set<(state: NavigationState) => void>} */
  #listeners = new Set()

  /** @type {NavigationState} */
  #state = {
    route: { path: '', search: '', params: null },
    component: null,
    is404: false,
    direction: 'forward',
  }

  /** @type {string|null} */
  #routeLoadingId = null

  /** @type {Array<(intent: NavigationIntent) => void>} */
  #beforeHooks = []

  /** @type {Array<(intent: NavigationIntent) => void>} */
  #afterHooks = []

  constructor() {
    this.#configs = routeConfig

    // Build NavGo route tuples: [pattern, config]
    const routes = this.#configs.map((c) => [c.path, c])

    this.#navgo = new Navgo(routes, {
      scroll_to_top: false,
      attach_to_window: false,
      preload_on_hover: false,
      before_navigate: (nav) => this.#onBeforeNavigate(nav),
      after_navigate: async (nav) => this.#onAfterNavigate(nav),
    })

    // Init NavGo (popstate listeners, process current URL)
    this.#navgo.init()
  }

  #can_prefetch(config) {
    if (config.meta?.auth && !session.is_authenticated) return false
    if (config.meta?.superuser && !session.is_superuser) return false
    return true
  }

  /** Warm only component chunks for likely next routes after the browser idles. */
  #schedule_neighbor_prefetch() {
    if (typeof window === 'undefined') return
    const run = () => this.#prefetch_neighbors()
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 3000 })
    } else {
      setTimeout(run, 1500)
    }
  }

  async #prefetch_neighbors() {
    await sessionReady()
    const neighbor_paths = new Set(getEdgesForPath(this.#state.route.path).map((edge) => edge.to))

    for (const config of this.#configs) {
      if (neighbor_paths.has(config.path) && this.#can_prefetch(config)) {
        config.load().catch(() => {})
      }
    }
  }

  // ── Public API ──

  /**
   * Navigate to a path (forward navigation).
   * @param {string} path
   */
  async navigate(path) {
    this.#routeLoadingId = loadingManager.start(LoadingType.Route, { source: path })
    loadingManager.updateProgress(this.#routeLoadingId, 0.2)
    // Fire declarative prefetch (component chunk + data) in parallel with the
    // NavGo transition so the target page's query cache is already warming
    // while the router swaps state. On hover we already prefetch; here we
    // cover programmatic navigate() + swipe-back + switcher clicks.
    this.prefetch(path).catch(() => {})
    try {
      await this.#navgo.goto(path)
    } catch (err) {
      console.error('[Navigation] goto failed:', err)
      this.#stopRouteLoading()
      throw err
    }
  }

  /** Whether the current entry has an earlier in-app history entry. */
  can_go_back() {
    return has_app_history()
  }

  /**
   * Retrace exact in-app history, replacing direct entries with a deliberate
   * fallback instead of leaving the app.
   * @param {string} [fallback]
   * @returns {boolean} whether browser history was used
   */
  back(fallback) {
    if (!this.can_go_back()) {
      if (fallback) this.replace(fallback)
      return false
    }

    this.#routeLoadingId = loadingManager.start(LoadingType.Route, { source: RouteSources.BACK })
    loadingManager.updateProgress(this.#routeLoadingId, 0.2)
    window.history.back()
    return true
  }

  /**
   * Replace current history entry and navigate.
   * @param {string} path
   */
  async replace(path) {
    this.prefetch(path).catch(() => {})
    await this.#navgo.goto(path, { replace: true })
  }

  /**
   * Swap the URL inside the route that is already mounted, without re-entering
   * it. For a page that pages through its own content — the reader's
   * prev/next — the route has not changed, only which record is on screen.
   *
   * `navigate()` would restart the route: the loading bar runs, the view
   * transition plays, and `RouteOutlet`'s `{#key path}` throws the mounted page
   * away and builds a new one around the new params. That is why paging through
   * questions felt like a page reload rather than a content change.
   *
   * The entry is replaced, not pushed, so Back still belongs to whatever opened
   * the page — which is what `back()` and every "Back" button on these pages
   * already assume. The address bar stays correct, so the record remains
   * deep-linkable and survives a refresh.
   *
   * Deliberately does NOT update `#state.route`: that is what `RouteOutlet`
   * keys on, so publishing the new path here would remount the page and undo
   * the point. A page that swaps its own URL owns its params from then on.
   *
   * @param {string} path
   */
  swapUrl(path) {
    this.#navgo.replace_state(path)
  }

  /**
   * Prefetch a route's component chunk + data via its declarative meta.prefetch.
   * @param {string} path
   */
  async prefetch(path) {
    await sessionReady()
    const match = await this.#navgo.match(path)
    const config = match?.route?.[1]
    if (!config?.load || !this.#can_prefetch(config)) return

    const component_promise = config.load()
    const prefetch = config.meta?.prefetch

    if (typeof prefetch === 'function') {
      const resources = prefetch(match?.params || {})
      for (const resource of Array.isArray(resources) ? resources : [resources]) {
        if (resource) createQuery(resource)
      }
    }

    await component_promise
  }

  /**
   * Subscribe to navigation state changes.
   * Immediately fires with current state.
   * @param {(state: NavigationState) => void} fn
   * @returns {() => void} unsubscribe
   */
  subscribe(fn) {
    this.#listeners.add(fn)
    fn(this.#state)
    return () => this.#listeners.delete(fn)
  }

  /**
   * Register a beforeNavigate hook.
   * @param {(intent: NavigationIntent) => void} fn
   */
  beforeNavigate(fn) {
    this.#beforeHooks.push(fn)
  }

  /**
   * Register an afterNavigate hook.
   * @param {(intent: NavigationIntent) => void} fn
   * @returns {() => void} unregister
   */
  afterNavigate(fn) {
    this.#afterHooks.push(fn)
    return () => {
      this.#afterHooks = this.#afterHooks.filter((hook) => hook !== fn)
    }
  }

  // ── Internal ──

  /**
   * NavGo before_navigate hook.
   * Fires after matching but before URL update.
   */
  #onBeforeNavigate(nav) {
    // Skip for initial navigation (no "from" route yet)
    if (this.#state.route.path === '') return

    // Persist scroll for current path before leaving
    saveScroll(this.#state.route.path)

    // Fire before hooks
    for (const hook of this.#beforeHooks) {
      hook({
        from: this.#state.route.path,
        to: nav.to?.path || location.pathname,
        direction: nav.type === 'popstate' ? 'backward' : 'forward',
        navigationType: 'page',
      })
    }
  }

  /**
   * NavGo after_navigate hook.
   * Fires after URL update and data loading.
   * Orchestrates component loading, transitions, scroll, focus.
   */
  async #onAfterNavigate(nav) {
    const to = nav.to
    const config = to?.route?.[1]

    // Determine directional semantic from navigation graph
    // popstate always maps to 'pop' (user went back/forward)
    const fromPath = nav.from?.path || this.#state.route.path || ''
    const toPath = to?.path || location.pathname
    const route_semantic = config?.meta?.transition === 'fade' ? 'swap' : 'push'
    const semantic = nav.type === 'popstate' ? 'pop' : getTransitionSemantic(fromPath, toPath, route_semantic)

    // ── 1. Handle 404 ──
    if (!config) {
      await runTransition(() => {
        this.#updateState({
          route: { path: to?.path || location.pathname, search: location.search, params: {} },
          component: null,
          is404: true,
          direction: semantic,
        })
      }, semantic)
      this.#stopRouteLoading()
      this.#notify_after_navigation(nav, semantic, 'page')
      return
    }

    // ── 1a. Superuser guard ──
    // meta.auth is enforced by App.svelte's Login gate — leaving the URL
    // intact lets the shell show Login and redirect back after sign-in.
    // Superuser routes still short-circuit to NotFound so non-admins
    // don't waste bandwidth on the admin chunk.
    if (session.loaded && config.meta?.superuser && !session.is_superuser) {
      await runTransition(() => {
        this.#updateState({
          route: { path: to.path || location.pathname, search: location.search, params: to.params || {} },
          component: null,
          is404: true,
          direction: semantic,
        })
      }, semantic)
      this.#stopRouteLoading()
      this.#notify_after_navigation(nav, semantic, 'page')
      return
    }

    // ── 2. Load component chunk ──
    if (this.#routeLoadingId) loadingManager.updateProgress(this.#routeLoadingId, 0.5)
    const loadPromise = config.load ? config.load() : null
    if (!loadPromise) return

    let Component
    try {
      const mod = await loadPromise
      // validate_params hook
      if (typeof mod.validate_params === 'function') {
        const valid = await mod.validate_params(to.params)
        if (!valid) {
          this.navigate('/')
          return
        }
      }
      Component = mod.default
      clearStaleChunkMarker()
    } catch (err) {
      // A chunk that cannot be fetched means the release moved under a
      // long-lived document, not that the route is missing. Reload onto the new
      // entry instead of accusing the user of a bad URL.
      if (recoverFromStaleChunk(err, to.path || location.pathname)) return
      console.error('[Navigation] Failed to load route:', err)
      this.#updateState({ is404: true, component: null, direction: semantic })
      this.#stopRouteLoading()
      return
    }

    // ── 3. Run transition + commit route ──
    if (this.#routeLoadingId) loadingManager.updateProgress(this.#routeLoadingId, 0.8)
    await runTransition(() => {
      this.#updateState({
        route: { path: location.pathname, search: location.search, params: to.params || {} },
        component: Component,
        is404: false,
        direction: semantic,
      })
    }, semantic)
    this.#stopRouteLoading()

    // Browser history restores context. Route metadata can opt forward
    // navigations into restoration; everything else starts at the top.
    if (semantic === 'pop' || config.meta?.scroll === 'restore') {
      restoreScroll(location.pathname)
    } else {
      resetScroll()
    }

    // ── 4. afterNavigate hooks ──
    this.#notify_after_navigation(nav, semantic, config.meta?.navigationType || 'page')
    this.#schedule_neighbor_prefetch()
  }

  #notify_after_navigation(nav, semantic, navigation_type) {
    for (const hook of this.#afterHooks) {
      hook({
        from: nav.from?.path || '',
        to: location.pathname,
        direction: semantic,
        navigationType: navigation_type,
      })
    }
  }

  /** Update state and notify all subscribers. */
  #updateState(partial) {
    this.#state = { ...this.#state, ...partial }
    for (const fn of this.#listeners) {
      fn(this.#state)
    }
  }

  /** Complete the current route loading entry. */
  #stopRouteLoading() {
    if (this.#routeLoadingId) {
      loadingManager.finish(this.#routeLoadingId)
      this.#routeLoadingId = null
    }
  }
}

/**
 * Singleton NavigationManager instance.
 */
export const navigationManager = new NavigationManager()
